import type {
  Card,
  ChapterSummary,
  Credential,
  CredentialList,
  LibraryItem,
  Region,
} from '@/shared/contracts';
import { readingSeconds } from '@/shared/library';
import { SPECIALIST_OPENS } from '@/shared/rubric';
import { formatDate } from './format';

/*
 * The one place the learner surfaces derive their numbers (design pass 3, Brendon 2026-09-22): Home,
 * Learn, Library, Record, the module page and the player bar all read what they show from here, so
 * a count can never say one thing on one screen and another elsewhere.
 *
 * Honest by construction. The server records a module's completion (its quiz passed) and the
 * learner's saved place in it, not each chapter, so a chapter counts as complete when its module is
 * complete, and an unfinished module shows where the learner is ("chapter 3 of 9"), never a guess
 * at how much of it was read. Nothing here simulates activity.
 */

/** The qualifications the app awards, in the order they open. */
export type Tier = 'foundation' | 'antarctica' | 'arctic';
export const TIERS: Tier[] = ['foundation', 'antarctica', 'arctic'];
export const TIER_NAME: Record<Tier, string> = {
  foundation: 'Foundation',
  antarctica: 'Antarctica Specialist',
  arctic: 'Arctic Specialist',
};

/** How a destination reads on a row; an item for no one destination reads "All destinations". */
export const DESTINATION_NAME: Record<Region, string> = { antarctica: 'Antarctica', arctic: 'Arctic' };
export const ALL_DESTINATIONS = 'All destinations';

export type Tone = 'faint' | 'accent' | 'success' | 'default' | 'warning';
export type Verb = 'Start' | 'Resume' | 'Review';

/** The modules, in curriculum order (the library already arrives in reading order). */
export const modulesOf = (library: LibraryItem[]): LibraryItem[] =>
  library.filter((item) => item.kind === 'module');

/**
 * The qualification a module counts toward: every core and operator module toward Foundation (the
 * server shows a learner only their own programme's operator module), a regional module toward its
 * region's Specialist. Anything that is not a module counts toward none.
 */
export function tierOf(item: Pick<LibraryItem, 'kind' | 'track' | 'region'>): Tier | null {
  if (item.kind !== 'module') return null;
  if (item.track === 'regional') return item.region;
  return 'foundation';
}

/** A module is complete when its quiz is passed; a brief, film or reference when its record says so. */
export function isComplete(item: LibraryItem): boolean {
  if (item.kind === 'module') return !!item.best_attempt?.passed || item.progress?.status === 'completed';
  return item.progress?.status === 'completed';
}

/** When it was completed: the first completion the server kept, else the passing attempt. */
export function completedOn(item: LibraryItem): string | null {
  if (!isComplete(item)) return null;
  return item.progress?.completed_at ?? item.best_attempt?.submitted_at ?? null;
}

const minutesOf = (seconds: number): number => Math.max(1, Math.round(seconds / 60));

/** Where a learner stands with one module, every figure from what the server holds. */
export type ModuleState = {
  /** Chapters in the module. */
  total: number;
  /** Chapters complete: all of them once the module is passed, none before. */
  complete: number;
  pct: number;
  /** The saved chapter (0-based position in `chapters`) while the module is under way, else null. */
  place: number | null;
  verb: Verb;
  /** The estimate for the whole module, and what is left from the saved position. */
  minutes: number;
  minutesLeft: number;
  /** The dated status sentence: statuses are sentences, actions are verbs. */
  status: string;
  tone: Tone;
};

export function moduleState(module: LibraryItem): ModuleState {
  const total = module.chapters.length;
  const done = isComplete(module);
  const runtime = readingSeconds(module.runtime_label, module.words);
  const minutes = minutesOf(runtime);
  const minutesLeft = minutesOf(Math.max(0, runtime - (module.progress?.position_s ?? 0)));
  const best = module.best_attempt;
  const underway = !done && module.progress !== null;
  const place =
    underway && total > 0 ? Math.min(Math.max(0, module.progress?.chapter_index ?? 0), total - 1) : null;
  const complete = done ? total : 0;
  const pct = total > 0 ? Math.round((complete / total) * 100) : done ? 100 : 0;
  if (done) {
    const on = formatDate(completedOn(module));
    return {
      total,
      complete,
      pct,
      place,
      verb: 'Review',
      minutes,
      minutesLeft,
      status: best?.passed
        ? `Passed ${best.score} of ${best.total}${on ? ` · ${on}` : ''}`
        : `Completed${on ? ` ${on}` : ''}`,
      tone: 'success',
    };
  }
  if (underway)
    return {
      total,
      complete,
      pct,
      place,
      verb: 'Resume',
      minutes,
      minutesLeft,
      status:
        place === null
          ? `In progress · about ${minutesLeft} min left`
          : `In progress · chapter ${place + 1} of ${total} · about ${minutesLeft} min left`,
      tone: 'accent',
    };
  if (best)
    return {
      total,
      complete,
      pct,
      place,
      // A failed quiz is real history, but it does not prove the learner saved a place in the
      // reading. With no progress record the reading still starts at the beginning.
      verb: 'Start',
      minutes,
      minutesLeft,
      status: `Attempted ${best.score} of ${best.total} · not yet passed`,
      tone: 'default',
    };
  return {
    total,
    complete,
    pct,
    place,
    verb: 'Start',
    minutes,
    minutesLeft,
    status: 'Not started',
    tone: 'faint',
  };
}

/** "0 of 9 chapters complete · 0%": the tier line a module card and page lead with. */
export const chapterLine = (state: Pick<ModuleState, 'complete' | 'total' | 'pct'>): string =>
  `${state.complete} of ${state.total} ${state.total === 1 ? 'chapter' : 'chapters'} complete · ${state.pct}%`;

/** Foundation as a count: required modules passed, and the chapters inside them. */
export type FoundationProgress = {
  modules: LibraryItem[];
  passed: number;
  total: number;
  pct: number;
  chapters: number;
  chaptersComplete: number;
};

export function foundationProgress(library: LibraryItem[]): FoundationProgress {
  const modules = modulesOf(library).filter((m) => tierOf(m) === 'foundation');
  const passed = modules.filter(isComplete).length;
  const chapters = modules.reduce((sum, m) => sum + m.chapters.length, 0);
  const chaptersComplete = modules.reduce((sum, m) => sum + moduleState(m).complete, 0);
  return {
    modules,
    passed,
    total: modules.length,
    pct: modules.length ? Math.round((passed / modules.length) * 100) : 0,
    chapters,
    chaptersComplete,
  };
}

/** One qualification as the learner holds it, or the honest reason it is not held yet. */
export type Qualification = {
  tier: Tier;
  name: string;
  /** The modules this programme requires for it. */
  modules: LibraryItem[];
  passed: number;
  total: number;
  /** The award, when there is one (the latest issued). */
  credential: Credential | null;
  state: 'earned' | 'available' | 'waiting';
  line: string;
};

const heldFor = (tier: Tier, credentials: CredentialList | undefined): Credential | null => {
  const matching = (credentials?.data ?? []).filter((c) =>
    tier === 'foundation' ? c.tier === 'foundation' : c.tier === 'specialist' && c.region === tier,
  );
  return matching.sort((a, b) => b.issued_at.localeCompare(a.issued_at))[0] ?? null;
};

/** A Foundation that can support a Specialist: held, not revoked and not expired. */
export const foundationCurrent = (credentials: CredentialList | undefined): boolean => {
  const foundation = heldFor('foundation', credentials);
  return foundation?.status === 'current' || foundation?.status === 'expiring';
};

function earnedLine(c: Credential): string {
  if (c.status === 'revoked') return 'Withdrawn by the academy';
  if (c.status === 'expired')
    return `Earned ${formatDate(c.issued_at)} · expired ${formatDate(c.valid_until)}`;
  return `Earned ${formatDate(c.issued_at)} · valid until ${formatDate(c.valid_until)}`;
}

const moduleCount = (n: number): string => `${n} required ${n === 1 ? 'module' : 'modules'}`;

/**
 * Foundation, then Antarctica and Arctic Specialist (the tiers that replace the mockup's levels),
 * each with true numbers. A Specialist tier is listed only where this programme has its module.
 */
export function qualifications(
  library: LibraryItem[],
  credentials: CredentialList | undefined,
): Qualification[] {
  const modules = modulesOf(library);
  const current = foundationCurrent(credentials);
  return TIERS.flatMap((tier): Qualification[] => {
    const required = modules.filter((m) => tierOf(m) === tier);
    if (tier !== 'foundation' && required.length === 0) return [];
    const passed = required.filter(isComplete).length;
    const credential = heldFor(tier, credentials);
    const base = {
      tier,
      name: TIER_NAME[tier],
      modules: required,
      passed,
      total: required.length,
      credential,
    };
    if (credential) return [{ ...base, state: 'earned', line: earnedLine(credential) }];
    if (tier === 'foundation')
      return [
        {
          ...base,
          state: 'available',
          line: `Not yet earned · ${passed} of ${moduleCount(required.length)} passed`,
        },
      ];
    if (current)
      return [
        {
          ...base,
          state: 'available',
          line: `Not yet earned · pass ${required.map((m) => m.title).join(' and ')} to earn it`,
        },
      ];
    return [
      {
        ...base,
        state: 'waiting',
        line: `${SPECIALIST_OPENS} · ${passed} of ${moduleCount(required.length)} passed`,
      },
    ];
  });
}

/**
 * A tier in a few words, for the lines under a module card and the tier cards on a module page:
 * "earned 20 Sep 2026", "0 of 5 required modules passed", "opens when Foundation is current".
 */
export function tierSummary(q: Qualification): string {
  if (q.credential) {
    if (q.credential.status === 'revoked') return 'withdrawn by the academy';
    if (q.credential.status === 'expired') return `expired ${formatDate(q.credential.valid_until)}`;
    return `earned ${formatDate(q.credential.issued_at)}`;
  }
  if (q.tier === 'foundation') return `${q.passed} of ${moduleCount(q.total)} passed`;
  if (q.state === 'available') return `pass ${q.modules.map((m) => m.title).join(' and ')} to earn it`;
  return SPECIALIST_OPENS.charAt(0).toLowerCase() + SPECIALIST_OPENS.slice(1);
}

/** Qualifications held today: earned, current or inside the renewal window. */
export const qualificationsHeld = (credentials: CredentialList | undefined): number =>
  (credentials?.data ?? []).filter((c) => c.status === 'current' || c.status === 'expiring').length;

/** A chapter the record lists as complete, dated by its module's completion. */
export type CompletedChapter = { module: LibraryItem; chapter: ChapterSummary; on: string };

/** Every complete chapter, newest first; chapters of one module keep their reading order. */
export function completedChapters(library: LibraryItem[]): CompletedChapter[] {
  return modulesOf(library)
    .filter(isComplete)
    .flatMap((module) =>
      module.chapters.map((chapter) => ({ module, chapter, on: completedOn(module) ?? '' })),
    )
    .sort(
      (a, b) =>
        b.on.localeCompare(a.on) ||
        a.module.sort_order - b.module.sort_order ||
        a.chapter.index - b.chapter.index,
    );
}

/**
 * The module a learner is on, for the start card and the player bar: the unfinished module they
 * actually have a saved place in, else the first module under way, else the next required
 * Foundation module, then the first other unfinished module. Null once every module is complete.
 */
export function currentModule(library: LibraryItem[], lastTouched: LibraryItem | null): LibraryItem | null {
  const modules = modulesOf(library);
  if (lastTouched?.kind === 'module') {
    const fresh = modules.find((m) => m.id === lastTouched.id);
    if (fresh && fresh.progress !== null && !isComplete(fresh)) return fresh;
  }
  return (
    modules.find((m) => m.progress !== null && !isComplete(m)) ??
    modules.find((m) => tierOf(m) === 'foundation' && !isComplete(m)) ??
    modules.find((m) => !isComplete(m)) ??
    null
  );
}

export type QueueEntry = { module: LibraryItem; chapter: ChapterSummary };

/**
 * The audio queue, "Up next": every unfinished chapter in global curriculum order. In the current
 * module, the current and earlier chapters are already positioned and omitted. Derived, not
 * stored: there is no server-held queue to reorder.
 */
export function upNext(library: LibraryItem[], current: LibraryItem | null): QueueEntry[] {
  const out: QueueEntry[] = [];
  for (const module of modulesOf(library)) {
    if (isComplete(module)) continue;
    const positioned = module.id === current?.id ? (moduleState(module).place ?? 0) + 1 : 0;
    for (const chapter of module.chapters.slice(positioned)) out.push({ module, chapter });
  }
  return out;
}

/** The module a brief, film or card belongs to: the course's own module number, else its region's. */
export function moduleOf(item: LibraryItem, library: LibraryItem[]): LibraryItem | null {
  if (item.kind === 'module') return item;
  const modules = modulesOf(library);
  const byNumber = item.module_num ? modules.find((m) => m.module_num === item.module_num) : undefined;
  if (byNumber) return byNumber;
  if (item.region) return modules.find((m) => m.track === 'regional' && m.region === item.region) ?? null;
  return null;
}

/** A module's own briefs, films and field cards, as the course relates them. */
export function moduleContents(
  module: LibraryItem,
  library: LibraryItem[],
  cards: Card[],
): { briefs: LibraryItem[]; films: LibraryItem[]; cards: Card[] } {
  const mine = library.filter((item) => item.kind !== 'module' && moduleOf(item, library)?.id === module.id);
  const briefs = mine.filter((item) => item.kind === 'brief');
  const briefIds = new Set(briefs.map((b) => b.id));
  return {
    briefs,
    films: mine.filter((item) => item.kind === 'film'),
    cards: cards.filter((card) => briefIds.has(card.item_id)),
  };
}

/** A recommendation and the reason it is made: never "Featured". */
export type Suggestion = { item: LibraryItem; reason: string };

const destinationIn = (specialty: string): Region | null =>
  /antarctic/i.test(specialty) ? 'antarctica' : /arctic/i.test(specialty) ? 'arctic' : null;

/**
 * "Chosen for you" from the destinations the advisor declared in their profile: each one that names
 * Antarctica or the Arctic (case-insensitive) chooses that destination's module, or once it is
 * complete the destination's next brief or film. With no match, the next required module.
 */
export function chosenForYou(library: LibraryItem[], specialties: string[]): Suggestion[] {
  const picks: Suggestion[] = [];
  const seen = new Set<Region>();
  for (const specialty of specialties) {
    const region = destinationIn(specialty);
    if (!region || seen.has(region)) continue;
    seen.add(region);
    const reason = `Because you listed ${specialty.trim()}`;
    const module = modulesOf(library).find((m) => m.track === 'regional' && m.region === region);
    const next = library.find(
      (item) =>
        (item.kind === 'brief' || item.kind === 'film') && item.region === region && !isComplete(item),
    );
    const item = module && !isComplete(module) ? module : (next ?? module);
    if (item) picks.push({ item, reason });
  }
  if (picks.length > 0) return picks;
  const required = modulesOf(library).find((m) => tierOf(m) === 'foundation' && !isComplete(m));
  if (required) return [{ item: required, reason: 'Because it is the next module required for Foundation' }];
  const any = modulesOf(library).find((m) => !isComplete(m));
  return any ? [{ item: any, reason: 'Because it is the next module in the course' }] : [];
}

/** The reference shelf's formats, in the order its chips and rows use. */
export type ShelfFormat = 'brief' | 'film' | 'reference' | 'card';
export const SHELF_FORMATS: ShelfFormat[] = ['brief', 'film', 'reference', 'card'];
export const SHELF_CHIP: Record<ShelfFormat, string> = {
  brief: 'Briefs',
  film: 'Films',
  reference: 'Reference',
  card: 'Field cards',
};
export const SHELF_LABEL: Record<ShelfFormat, string> = {
  brief: 'Field brief',
  film: 'Film',
  reference: 'Reference',
  card: 'Field card',
};

/** Where an item applies: a region, or every destination. */
export type Destination = Region | 'all';

/** One row of the reference shelf. */
export type ShelfRow = {
  key: string;
  format: ShelfFormat;
  id: string;
  title: string;
  destination: Destination;
  topic: string;
  /** "about 5 min" for reading; a field card is one screen. */
  duration: string;
  to: string;
  verb: string;
  /** Done: a brief, film or reference completed; a card open. Counted out of the chips. */
  done: boolean;
  status: string;
  tone: Tone;
  /** The field card a Save keeps: a brief saves its own card. Null where nothing can be saved. */
  cardId: string | null;
  saved: boolean;
  /** A locked card names the brief that opens it. */
  unlockedBy: LibraryItem | null;
  /** On the default shelf, a locked card is one line on the brief that will unlock it. */
  nestedCard: { id: string; title: string } | null;
};

const destinationOf = (item: Pick<LibraryItem, 'region'>): Destination => item.region ?? 'all';

/** The topic beside the destination: the module the item belongs to. */
function topicOf(item: LibraryItem, library: LibraryItem[]): string {
  if (item.kind === 'certification') return 'Certification';
  const module = moduleOf(item, library);
  if (!module) return 'Reference';
  // A destination module's title is the destination itself; name it as the module so the row does
  // not read "Antarctica · Antarctica".
  return module.region && module.title === DESTINATION_NAME[module.region]
    ? `${module.title} module`
    : module.title;
}

function readingStatus(item: LibraryItem): { status: string; tone: Tone } {
  if (isComplete(item)) {
    const on = formatDate(completedOn(item));
    return { status: on ? `Completed ${on}` : 'Completed', tone: 'success' };
  }
  if (item.progress) {
    const left = minutesOf(
      Math.max(0, readingSeconds(item.runtime_label, item.words) - item.progress.position_s),
    );
    return { status: `In progress · about ${left} min left`, tone: 'accent' };
  }
  if (item.best_attempt) return { status: 'Check attempted', tone: 'default' };
  return { status: 'Not started', tone: 'faint' };
}

/**
 * The reference shelf: the 24 field briefs, the films, the certification reference and the 24
 * field cards. A card still locked stays on the shelf as a row naming the brief that opens it.
 */
export function shelfRows(library: LibraryItem[], cards: Card[]): ShelfRow[] {
  const saved = new Set(cards.filter((c) => c.saved).map((c) => c.id));
  const cardByBrief = new Map(cards.map((card) => [card.item_id, card]));
  const rows: ShelfRow[] = [];
  for (const item of library) {
    if (item.kind === 'module') continue;
    const format: ShelfFormat = item.kind === 'brief' ? 'brief' : item.kind === 'film' ? 'film' : 'reference';
    const { status, tone } = readingStatus(item);
    rows.push({
      key: `${format}:${item.id}`,
      format,
      id: item.id,
      title: item.title,
      destination: destinationOf(item),
      topic: topicOf(item, library),
      duration: `about ${minutesOf(readingSeconds(item.runtime_label, item.words))} min`,
      to: `/learn/${encodeURIComponent(item.id)}`,
      // A film here is its script: no film has a recording or a video, so nothing says "Watch".
      verb: item.kind === 'film' && item.has_audio ? 'Listen' : 'Read',
      done: isComplete(item),
      status,
      tone,
      cardId: item.kind === 'brief' ? item.card_id : null,
      saved: item.card_id !== null && item.kind === 'brief' && saved.has(item.card_id),
      unlockedBy: null,
      nestedCard:
        item.kind === 'brief' && cardByBrief.get(item.id)?.locked
          ? {
              id: cardByBrief.get(item.id)!.id,
              title: cardByBrief.get(item.id)!.title,
            }
          : null,
    });
  }
  for (const card of [...cards].sort((a, b) => a.sort_order - b.sort_order)) {
    const brief = library.find((item) => item.id === card.item_id) ?? null;
    rows.push({
      key: `card:${card.id}`,
      format: 'card',
      id: card.id,
      title: card.title,
      destination: brief ? destinationOf(brief) : 'all',
      topic: brief ? topicOf(brief, library) : 'Reference',
      duration: 'one screen',
      to:
        card.locked && brief
          ? `/learn/${encodeURIComponent(brief.id)}`
          : `/cards/${encodeURIComponent(card.id)}`,
      verb: card.locked ? 'Read brief' : 'Open card',
      done: !card.locked,
      status: card.locked
        ? `Locked · finish ${brief ? `${brief.id} ${brief.title}` : 'its brief'} to open it`
        : 'Open · unlocked by its brief',
      tone: card.locked ? 'faint' : 'success',
      cardId: card.id,
      saved: card.saved,
      unlockedBy: card.locked ? brief : null,
      nestedCard: null,
    });
  }
  return rows;
}
